import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Star, Camera, X, Send, Loader2, ChevronRight, Share2, ShieldCheck } from 'lucide-react';
import { supabase, Review } from '@/lib/supabase';
import { ROUTE_PATHS } from '@/lib/index';
import { springPresets, staggerContainer, staggerItem } from '@/lib/motion';
import { useAuth } from '@/hooks/useAuth';

function Stars({ rating, onChange }: { rating: number; onChange?: (n: number) => void }) {
  return (
    <div className="flex gap-1">
      {[1,2,3,4,5].map(n => (
        <button key={n} type="button" onClick={() => onChange?.(n)}
          className={onChange ? 'cursor-pointer hover:scale-110 transition-transform' : 'cursor-default'}>
          <Star className={`w-7 h-7 ${n <= rating ? 'fill-amber-400 text-amber-400' : 'text-muted-foreground/40'}`} />
        </button>
      ))}
    </div>
  );
}

async function shareReview(r: Review) {
  const lines = [
    `🍽️ ${r.meal_name} — ${r.rating}/5 ⭐`,
    r.store_name ? `📍 ${r.store_name}` : '',
    r.comment ? `"${r.comment}"` : '',
    '',
    '📲 Made with FlyersFeast — real grocery deals, real recipes',
  ].filter(Boolean).join('\n');
  if (navigator.share) {
    try { await navigator.share({ title: `${r.meal_name} Review`, text: lines }); return; } catch { /**/ }
  }
  try { await navigator.clipboard.writeText(lines); } catch { /**/ }
  const el = document.createElement('textarea');
  el.value = lines; el.style.cssText = 'position:fixed;top:-9999px;opacity:0;';
  document.body.appendChild(el); el.select();
  try { document.execCommand('copy'); } catch { /**/ }
  document.body.removeChild(el);
}

export default function ReviewsPage() {
  const navigate  = useNavigate();
  const fileRef   = useRef<HTMLInputElement>(null);
  const { user, profile, setShowAuthModal } = useAuth();

  const [reviews,      setReviews]      = useState<Review[]>([]);
  const [loadingR,     setLoadingR]     = useState(true);
  const [showForm,     setShowForm]     = useState(false);
  const [posterName,   setPosterName]   = useState('');
  const [mealName,     setMealName]     = useState('');
  const [storeName,    setStoreName]    = useState('');
  const [rating,       setRating]       = useState(5);
  const [comment,      setComment]      = useState('');
  const [photoFile,    setPhotoFile]    = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [submitting,   setSubmitting]   = useState(false);
  const [error,        setError]        = useState('');
  const [sharedId,     setSharedId]     = useState<string | null>(null);

  // Pre-fill name from auth
  useEffect(() => {
    if (user && profile?.display_name) setPosterName(profile.display_name);
    else if (user?.email) setPosterName(user.email.split('@')[0]);
  }, [user, profile]);

  useEffect(() => { loadReviews(); }, []);

  const loadReviews = async () => {
    setLoadingR(true);
    const { data } = await supabase
      .from('dish_reviews')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(50);
    setReviews((data as Review[]) ?? []);
    setLoadingR(false);
  };

  const handlePhoto = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setPhotoFile(f);
    const reader = new FileReader();
    reader.onload = ev => setPhotoPreview(ev.target?.result as string);
    reader.readAsDataURL(f);
  };

  const handleSubmit = async () => {
    if (!mealName.trim()) { setError('Please enter the dish name.'); return; }
    setError('');
    setSubmitting(true);
    try {
      let photoUrl: string | null = null;
      if (photoFile) {
        const ext  = photoFile.name.split('.').pop()?.toLowerCase() ?? 'jpg';
        const path = `anon/${Date.now()}_${Math.random().toString(36).slice(2,8)}.${ext}`;
        const { error: upErr } = await supabase.storage
          .from('review-photos')
          .upload(path, photoFile, { upsert: false, contentType: photoFile.type });
        if (upErr) {
          setError(`Photo upload failed: ${upErr.message}. Posting without photo.`);
          await new Promise(r => setTimeout(r, 1800));
          setError('');
        } else {
          const { data: urlData } = supabase.storage.from('review-photos').getPublicUrl(path);
          photoUrl = urlData.publicUrl;
        }
      }
      const { error: insErr } = await supabase.from('dish_reviews').insert({
        user_id:    user?.id ?? '00000000-0000-0000-0000-000000000000',
        meal_name:  mealName.trim(),
        store_name: storeName.trim() || null,
        rating,
        comment:    comment.trim() || null,
        photo_url:  photoUrl,
        user_name:  posterName.trim() || 'Anonymous',
        user_avatar: null,
      });
      if (insErr) throw insErr;
      setShowForm(false);
      resetForm();
      loadReviews();
    } catch (e) {
      setError((e as Error).message?.slice(0,120) ?? 'Failed to post. Try again.');
    } finally { setSubmitting(false); }
  };

  const resetForm = () => {
    setMealName(''); setStoreName(''); setRating(5);
    setComment(''); setPhotoFile(null); setPhotoPreview(null);
    setError('');
    if (user && profile?.display_name) setPosterName(profile.display_name);
    else if (user?.email) setPosterName(user.email.split('@')[0]);
    else setPosterName('');
  };

  const handleShare = async (r: Review) => {
    setSharedId(r.id);
    await shareReview(r);
    setTimeout(() => setSharedId(null), 2500);
  };

  return (
    <div className="min-h-screen bg-background pt-12 pb-24">
      <header className="sticky top-12 z-10 bg-background/95 backdrop-blur border-b border-border/50">
        <div className="container mx-auto px-4 h-14 flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="text-muted-foreground hover:text-foreground">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <span className="font-bold text-foreground flex-1">Community Reviews</span>
          <button
            onClick={() => { resetForm(); setShowForm(true); }}
            className="flex items-center gap-1.5 text-xs font-bold bg-primary text-primary-foreground px-3 py-2 rounded-full hover:opacity-90 transition-opacity"
          >
            <Camera className="w-3.5 h-3.5" /> Post a Review
          </button>
        </div>
      </header>

      {/* Post review sheet */}
      <AnimatePresence>
        {showForm && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/60 flex items-end justify-center"
            onClick={e => { if (e.target === e.currentTarget) setShowForm(false); }}
          >
            <motion.div
              initial={{ y: '100%' }} animate={{ y: 0 }} exit={{ y: '100%' }}
              transition={springPresets.snappy}
              className="bg-background w-full max-w-xl rounded-t-3xl p-6 pb-10 max-h-[92vh] overflow-y-auto"
            >
              <div className="flex items-center justify-between mb-5">
                <div>
                  <h2 className="text-lg font-bold text-foreground">Share Your Dish 📸</h2>
                  {user && <p className="text-xs text-primary font-medium mt-0.5">✓ Posting as {posterName}</p>}
                </div>
                <button onClick={() => setShowForm(false)} className="text-muted-foreground hover:text-foreground">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-3.5">
                <div
                  onClick={() => fileRef.current?.click()}
                  className="relative w-full aspect-video rounded-2xl border-2 border-dashed border-border hover:border-primary/50 bg-muted/30 flex items-center justify-center cursor-pointer overflow-hidden transition-colors group"
                >
                  {photoPreview
                    ? <img src={photoPreview} className="w-full h-full object-cover" alt="preview" />
                    : (
                      <div className="text-center group-hover:scale-105 transition-transform">
                        <Camera className="w-10 h-10 text-muted-foreground/50 mx-auto mb-2" />
                        <p className="text-sm font-semibold text-muted-foreground">Tap to take or upload a photo</p>
                        <p className="text-xs text-muted-foreground/60 mt-1">Optional but encouraged!</p>
                      </div>
                    )}
                  <input ref={fileRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={handlePhoto} />
                </div>

                <input type="text" value={mealName} onChange={e => setMealName(e.target.value)}
                  placeholder="Dish name — e.g. Korean BBQ Bowl *"
                  className="w-full h-12 px-4 rounded-xl border border-border bg-background text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
                />
                <input type="text" value={storeName} onChange={e => setStoreName(e.target.value)}
                  placeholder="Store — e.g. Sobeys (optional)"
                  className="w-full h-12 px-4 rounded-xl border border-border bg-background text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
                />
                {!user && (
                  <div>
                    <input type="text" value={posterName} onChange={e => setPosterName(e.target.value)}
                      placeholder="Your name or nickname (optional)"
                      className="w-full h-12 px-4 rounded-xl border border-border bg-background text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
                    />
                    <button onClick={() => { setShowForm(false); setShowAuthModal(true); }}
                      className="text-xs text-primary font-medium mt-1.5 hover:underline">
                      Sign in to get a verified badge →
                    </button>
                  </div>
                )}

                <div>
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Rating</p>
                  <Stars rating={rating} onChange={setRating} />
                </div>

                <textarea value={comment} onChange={e => setComment(e.target.value)}
                  placeholder="How did it turn out? Any tips for others? (optional)"
                  rows={3}
                  className="w-full px-4 py-3 rounded-xl border border-border bg-background text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 resize-none"
                />

                {error && <p className="text-destructive text-xs font-medium">⚠ {error}</p>}

                <button onClick={handleSubmit} disabled={submitting}
                  className="w-full h-12 rounded-xl bg-primary text-primary-foreground font-bold text-sm flex items-center justify-center gap-2 hover:opacity-90 disabled:opacity-60 transition-opacity">
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4" /> Post to Community</>}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="container mx-auto max-w-xl px-4 py-5">
        <motion.div
          {...{initial:{opacity:0,y:12},animate:{opacity:1,y:0},transition:springPresets.gentle}}
          className="bg-primary/8 border border-primary/20 rounded-2xl p-4 mb-5 flex items-center gap-4"
        >
          <div className="text-3xl">📸</div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold text-foreground">Made something? Share it!</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              {user ? `Posting as ${posterName}` : 'Post a photo + rating — no sign-in needed'}
            </p>
          </div>
          <button onClick={() => { resetForm(); setShowForm(true); }}
            className="text-xs font-bold bg-primary text-primary-foreground px-3 py-2 rounded-full hover:opacity-90 shrink-0">
            Post
          </button>
        </motion.div>

        {loadingR ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : reviews.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-5xl mb-4">🍽️</p>
            <p className="text-base font-bold text-foreground mb-1">No reviews yet</p>
            <p className="text-sm text-muted-foreground mb-6">Be the first to share a dish you made!</p>
            <button onClick={() => { resetForm(); setShowForm(true); }}
              className="bg-primary text-primary-foreground font-bold text-sm px-6 py-3 rounded-full hover:opacity-90">
              Post First Review
            </button>
          </div>
        ) : (
          <motion.div variants={staggerContainer} initial="hidden" animate="visible" className="space-y-4">
            {reviews.map(r => (
              <motion.div key={r.id} variants={staggerItem}
                className="bg-card border border-border rounded-2xl overflow-hidden">
                {r.photo_url && (
                  <div className="aspect-video overflow-hidden bg-muted">
                    <img src={r.photo_url} alt={r.meal_name}
                      className="w-full h-full object-cover" loading="lazy" />
                  </div>
                )}
                <div className="p-4">
                  <div className="flex items-start justify-between gap-2 mb-1.5">
                    <div className="min-w-0 flex-1">
                      <h3 className="font-bold text-foreground truncate">{r.meal_name}</h3>
                      {r.store_name && <p className="text-xs text-muted-foreground">📍 {r.store_name}</p>}
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <Stars rating={r.rating} />
                    </div>
                  </div>
                  {r.comment && (
                    <p className="text-sm text-foreground/80 leading-relaxed mt-2 mb-3">{r.comment}</p>
                  )}
                  <div className="flex items-center gap-2 mt-2">
                    <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center text-[10px] font-bold text-primary shrink-0">
                      {(r.user_name ?? 'A')[0].toUpperCase()}
                    </div>
                    <span className="text-xs text-muted-foreground flex-1">{r.user_name ?? 'Anonymous'}</span>
                    {/* verified badge for logged-in posters */}
                    {r.user_id && r.user_id !== '00000000-0000-0000-0000-000000000000' && (
                      <span className="flex items-center gap-0.5 text-[10px] font-bold text-primary">
                        <ShieldCheck className="w-3 h-3" /> Verified
                      </span>
                    )}
                    <span className="text-xs text-muted-foreground">
                      {new Date(r.created_at).toLocaleDateString('en-CA',{month:'short',day:'numeric'})}
                    </span>
                    <button onClick={() => handleShare(r)}
                      className="flex items-center gap-1 text-xs font-semibold text-primary hover:underline ml-2">
                      <Share2 className="w-3.5 h-3.5" />
                      {sharedId === r.id ? 'Shared!' : 'Share'}
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        )}

        <div className="mt-10 text-center">
          <button onClick={() => navigate(ROUTE_PATHS.HOME)}
            className="flex items-center gap-1.5 text-sm text-primary font-semibold mx-auto hover:underline">
            Plan your next meal <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
